"use client";

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { AuthApiError, AuthStatus, getAuthContext, switchActiveOrganization, WorkspaceContext } from "@/lib/auth";

interface AuthContextValue {
  status: AuthStatus;
  context: WorkspaceContext | null;
  user: WorkspaceContext["user"] | null;
  activeOrganization: WorkspaceContext["active_organization"];
  organizations: WorkspaceContext["organizations"];
  error: AuthApiError | null;
  refresh: () => Promise<void>;
  isSwitchingOrganization: boolean;
  switchOrganization: (ssoOrganizationId: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [status, setStatus] = useState<AuthStatus>("loading");
  const [context, setContext] = useState<WorkspaceContext | null>(null);
  const [error, setError] = useState<AuthApiError | null>(null);
  const [isSwitchingOrganization, setIsSwitchingOrganization] = useState(false);

  const refresh = useCallback(async () => {
    setStatus("loading");
    setError(null);

    try {
      const nextContext = await getAuthContext();
      setContext(nextContext);
      setStatus("authenticated");
    } catch (caughtError) {
      const authError = caughtError instanceof AuthApiError
        ? caughtError
        : new AuthApiError("Unable to authenticate the user.", 0);

      setContext(null);
      setError(authError);
      setStatus(authError.status === 401 ? "unauthenticated" : authError.status === 403 ? "forbidden" : "error");
    }
  }, []);

  const switchOrganization = useCallback(async (ssoOrganizationId: string) => {
    setIsSwitchingOrganization(true);

    try {
      const nextContext = await switchActiveOrganization(ssoOrganizationId);
      setContext(nextContext);
      setError(null);
    } finally {
      setIsSwitchingOrganization(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const value = useMemo<AuthContextValue>(() => ({
    status,
    context,
    user: context?.user ?? null,
    activeOrganization: context?.active_organization ?? null,
    organizations: context?.organizations ?? [],
    error,
    refresh,
    isSwitchingOrganization,
    switchOrganization,
  }), [context, error, isSwitchingOrganization, refresh, status, switchOrganization]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }

  return context;
}
